import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Typography,
  useTheme,
  Skeleton,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider
} from '@mui/material';
import { TrendingUp, TrendingDown, Speed, InfoOutlined } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { useItemVelocity } from '../hooks/useDashboardData';
import type { ItemVelocityItem } from '../types/dashboard.types';

export const ItemVelocity: React.FC = () => {
  const theme = useTheme();
  const { data, isLoading, error } = useItemVelocity(30, 1, 10);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ItemVelocityItem | null>(null);

  // Sort items by velocity to get fast and slow movers - with safety checks
  const fastMovers = data?.items && Array.isArray(data.items)
    ? data.items
        .slice()
        .sort((a, b) => b.averageDailyMovement - a.averageDailyMovement)
        .slice(0, 5)
    : [];

  const slowMovers = data?.items && Array.isArray(data.items)
    ? data.items
        .slice()
        .sort((a, b) => a.averageDailyMovement - b.averageDailyMovement)
        .slice(0, 5)
    : [];

  const handleOpenDetails = (item: ItemVelocityItem) => {
    setSelectedItem(item);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedItem(null);
  };

  return (
    <Card
      sx={{
        borderRadius: theme.general.borderRadius,
        boxShadow: theme.colors.shadows.card
      }}
    >
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Speed />
            <Typography variant="h4" fontWeight={700}>
              Item Velocity (Last 30 Days)
            </Typography>
          </Box>
        }
        subheader={
          <Typography variant="body2" color="text.secondary">
            Movement analysis by daily average
          </Typography>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', gap: 3 }}>
            {[1, 2].map((i) => (
              <Box key={i} sx={{ flex: 1 }}>
                <Skeleton width="60%" sx={{ mb: 1 }} />
                {Array.from(new Array(5)).map((_, index) => (
                  <Skeleton key={index} width="100%" height={60} sx={{ mb: 1 }} />
                ))}
              </Box>
            ))}
          </Box>
        ) : error ? (
          <Typography color="error">Failed to load data</Typography>
        ) : data && data.items.length > 0 ? (
          <Box sx={{ display: 'flex', gap: 3, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
            {/* Fast Movers */}
            <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 0 } }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 2,
                  pb: 1,
                  borderBottom: `2px solid ${theme.palette.success.main}`
                }}
              >
                <TrendingUp sx={{ color: theme.palette.success.main }} />
                <Typography variant="subtitle1" fontWeight={700} color="success.main">
                  Fast Movers
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {fastMovers.map((item, index) => (
                  <Box
                    key={item.unitId}
                    sx={{
                      p: 1.5,
                      borderRadius: theme.general.borderRadiusSm,
                      backgroundColor: theme.colors.alpha.success[10],
                      border: `1px solid ${theme.colors.alpha.success[30]}`,
                      position: 'relative'
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      <Chip
                        label={`#${index + 1}`}
                        size="small"
                        sx={{
                          backgroundColor: theme.palette.success.main,
                          color: '#fff',
                          height: 20,
                          fontSize: '0.7rem',
                          fontWeight: 700
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDetails(item)}
                        sx={{
                          width: 20,
                          height: 20,
                          color: theme.palette.success.main,
                          '&:hover': {
                            backgroundColor: theme.colors.alpha.success[30]
                          }
                        }}
                      >
                        <InfoOutlined sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        pr: 5
                      }}
                    >
                      {item.itemName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      {item.barcode}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" fontWeight={600} color="success.main">
                        {item.averageDailyMovement.toFixed(1)} units/day
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.totalMovements} movements
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      On Hand: {item.currentOnHand.toLocaleString()}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Slow Movers */}
            <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 0 } }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 2,
                  pb: 1,
                  borderBottom: `2px solid ${theme.palette.warning.main}`
                }}
              >
                <TrendingDown sx={{ color: theme.palette.warning.main }} />
                <Typography variant="subtitle1" fontWeight={700} color="warning.main">
                  Slow Movers
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {slowMovers.map((item, index) => (
                  <Box
                    key={item.unitId}
                    sx={{
                      p: 1.5,
                      borderRadius: theme.general.borderRadiusSm,
                      backgroundColor: theme.colors.alpha.warning[10],
                      border: `1px solid ${theme.colors.alpha.warning[30]}`,
                      position: 'relative'
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      <Chip
                        label={`#${index + 1}`}
                        size="small"
                        sx={{
                          backgroundColor: theme.palette.warning.main,
                          color: '#fff',
                          height: 20,
                          fontSize: '0.7rem',
                          fontWeight: 700
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDetails(item)}
                        sx={{
                          width: 20,
                          height: 20,
                          color: theme.palette.warning.main,
                          '&:hover': {
                            backgroundColor: theme.colors.alpha.warning[30]
                          }
                        }}
                      >
                        <InfoOutlined sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        pr: 5
                      }}
                    >
                      {item.itemName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      {item.barcode}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" fontWeight={600} color="warning.main">
                        {item.averageDailyMovement.toFixed(1)} units/day
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.totalMovements} movements
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      On Hand: {item.currentOnHand.toLocaleString()}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        ) : (
          <Typography color="text.secondary" textAlign="center" sx={{ py: 3 }}>
            No velocity data available
          </Typography>
        )}
      </CardContent>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetails}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" fontWeight={700}>
              Item Details
            </Typography>
            <IconButton onClick={handleCloseDetails} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedItem && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Basic Info */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Item Name
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {selectedItem.itemName}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Barcode
                  </Typography>
                  <Typography variant="body2">{selectedItem.barcode}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Symbol
                  </Typography>
                  <Typography variant="body2">{selectedItem.symbol}</Typography>
                </Box>
              </Box>

              <Divider />

              {/* Velocity Metrics */}
              <Typography variant="h6" fontWeight={700}>
                Velocity Metrics
              </Typography>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Avg. Daily Movement
                  </Typography>
                  <Chip
                    label={`${selectedItem.averageDailyMovement.toFixed(2)} units/day`}
                    color="primary"
                    size="small"
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Total Movements
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {selectedItem.totalMovements}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Quantity Moved
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {selectedItem.quantityMoved.toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Current On Hand
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {selectedItem.currentOnHand.toLocaleString()}
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Period
                </Typography>
                <Typography variant="body2">
                  {selectedItem.daysInPeriod} days
                </Typography>
              </Box>

              {/* Additional Properties */}
              {selectedItem.additionalProperties &&
                Object.keys(selectedItem.additionalProperties).length > 0 && (
                  <>
                    <Divider />
                    <Typography variant="h6" fontWeight={700}>
                      Additional Properties
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {Object.entries(selectedItem.additionalProperties).map(([key, value]) => (
                        <Box key={key} sx={{ display: 'flex', gap: 1 }}>
                          <Typography variant="body2" fontWeight={600} sx={{ minWidth: 100 }}>
                            {key}:
                          </Typography>
                          <Typography variant="body2">{value}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};
